import { useState, useRef, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconChevronDown } from '../../UI';
import styles from '../InterventionGraphEditor.module.css';

// Custom select that portals its list to document.body. Needed because the
// chain row container uses overflow-x:auto (implying overflow-y:auto), which
// clips absolutely-positioned children like the default Dropdown's list.

interface PortalSelectProps<T> {
  options: T[];
  selected: T | null;
  getKey: (option: T) => string;
  renderOption: (option: T) => ReactNode;
  onChange: (value: T) => void;
  placeholder: string;
  disabledKeys?: Set<string>;
  disabled?: boolean;
}

export function PortalSelect<T>({
  options,
  selected,
  getKey,
  renderOption,
  onChange,
  placeholder,
  disabledKeys,
  disabled
}: Readonly<PortalSelectProps<T>>) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!open) return;
    const update = () => {
      if (buttonRef.current) setRect(buttonRef.current.getBoundingClientRect());
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={styles.selectBtn}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <span className={`${styles.selectText} ${selected ? '' : styles.selectPlaceholder}`}>
          {selected ? renderOption(selected) : placeholder}
        </span>
        <IconChevronDown width={12} height={12} />
      </button>

      {open &&
        rect &&
        createPortal(
          <div
            ref={listRef}
            className={styles.selectList}
            data-floating-child="true"
            style={{
              position: 'fixed',
              top: rect.bottom + 4,
              left: rect.left,
              minWidth: rect.width
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {options.map((opt) => {
              const key = getKey(opt);
              const isDisabled = disabledKeys?.has(key) ?? false;
              const isSelected = selected != null && getKey(selected) === key;
              return (
                <button
                  key={key}
                  type="button"
                  disabled={isDisabled}
                  className={`${styles.selectItem} ${isSelected ? styles.selectItemActive : ''}`}
                  onClick={() => {
                    onChange(opt);
                    setOpen(false);
                  }}
                >
                  {renderOption(opt)}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}
